import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Response,
  BaseTransaction,
  Pagination,
  TransactionWarning,
  TransactionWarningActionEnum,
} from '@ui/src';
import { customersComponentApi } from '../../../customers/store/services/customers';
import {
  AccountsCurrencies,
  AccountsFlows,
  AffilatedCompany,
  CRSAndFatca,
  DictionaryItem,
  ExpectedFundsOrigins,
} from '../../../new-application/types';
import {
  CustomerScoreQueuedProps,
  CustomerScore,
  CustomerScoreUpdateStatus,
  OngoingMonitring,
  TransactionMonitoringRule,
} from '../../types';

export interface CustomerAmlState {
  aml?: CustomerScore;
  lastCalculatingAml?: CustomerScore;
  amlUpdateStatus?: CustomerScoreUpdateStatus;
  amlTransactions?: BaseTransaction[];
  pagination?: Pagination;
  amlTransactionsErrorsList?: string;
  nextReviewDate?: string;
  ongoingMonitoring?: OngoingMonitring[];
  ongoingMonitoringPagination?: Pagination;
  ongoingMonitoringErrorsList?: string;
  ongoingMonitoringItem?: OngoingMonitring;
  amlChangeLogs?: CustomerScore[];
  amlChangeLogsPagination?: Pagination;
  accountsCurrencies?: AccountsCurrencies[];
  accountsFlows?: AccountsFlows[];
  expectedFundsOrigins?: ExpectedFundsOrigins[];
  sourceOfWealth?: DictionaryItem[];
  sourceOfIncome?: DictionaryItem[];
  sourceOfWealthForIndividual?: DictionaryItem[];
  customerSourceOfFunds?: DictionaryItem[];
  crsAndFatca?: CRSAndFatca[];
  affiliatedCompanies?: AffilatedCompany[];
  relationshipToTheCustomerDictionary?: DictionaryItem[];
  businessActivities?: DictionaryItem[];
  customerMainClients?: DictionaryItem[];
  salesChannels?: DictionaryItem[];
  customerSourceOfWealth?: DictionaryItem[];
  annualIncomes?: DictionaryItem[];
  typeOfAccounts?: DictionaryItem[];
  monitoringRules?: TransactionMonitoringRule[];
  monitoringRulesErrorsList?: string;
}

const initialState: CustomerAmlState = {};

export const customerAmlSlice = createSlice({
  name: 'individualCustomerAml',
  initialState,
  reducers: {
    clearAmlState: (state) => {
      state.aml = undefined;
      state.lastCalculatingAml = undefined;
      state.amlUpdateStatus = undefined;
      state.ongoingMonitoring = undefined;
      state.ongoingMonitoringErrorsList = undefined;
      state.ongoingMonitoringItem = undefined;
      state.ongoingMonitoringPagination = undefined;
      state.accountsCurrencies = undefined;
      state.accountsFlows = undefined;
      state.expectedFundsOrigins = undefined;
      state.crsAndFatca = undefined;
      state.affiliatedCompanies = undefined;
      state.monitoringRules = undefined;
      state.monitoringRulesErrorsList = undefined;
    },
    clearAmlChangeLogs: (state) => {
      state.amlChangeLogs = undefined;
      state.amlChangeLogsPagination = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerOngoingWarning.matchPending,
      (state) => {
        state.ongoingMonitoringErrorsList = undefined;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerOngoingWarning.matchRejected,
      (state, action: any) => {
        state.ongoingMonitoring = undefined;
        state.ongoingMonitoringPagination = undefined;

        if (action?.payload.data) {
          if (Array.isArray(action?.payload.data?.message)) {
            state.ongoingMonitoringErrorsList = action?.payload.data?.message
              ?.map((item: { value: string }) => item.value)
              .join('; ');
          } else {
            state.ongoingMonitoringErrorsList = action?.payload.data?.error;
          }
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerOngoingWarning.matchFulfilled,
      (
        state,
        action: PayloadAction<{ data: TransactionWarning }> & {
          meta: {
            arg: {
              originalArgs: {
                id: string;
                warningId: string;
              };
            };
          };
        },
      ) => {
        const nextWarnings = state.ongoingMonitoring?.map((item) => {
          const result = { ...item };

          if (item.id === action.meta.arg.originalArgs.id) {
            const nextGroupWarnings = item.warnings?.edges?.map((warning) => {
              const warningResult = { ...warning };

              if (
                warningResult.node.id === action.meta.arg.originalArgs.warningId
              ) {
                warningResult.node.blacklistDoc =
                  action.payload.data.blacklistDoc;
                warningResult.node.blacklistItem =
                  action.payload.data.blacklistItem;
              }

              return warningResult;
            });

            result.warnings = {
              edges: nextGroupWarnings,
              pageInfo: result.warnings.pageInfo,
            };
          }

          return result;
        });

        state.ongoingMonitoring = nextWarnings;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerUserScore.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerScore }>) => {
        state.aml = action.payload.data;

        if (action?.payload?.data?.createdAt) {
          const date = new Date(action.payload.data.createdAt);

          const nextReviewDate = new Date(
            date.setFullYear(date.getFullYear() + 1),
          );

          state.nextReviewDate = nextReviewDate.toISOString();
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerUserScore.matchRejected,
      (state) => {
        state.aml = undefined;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerAmlUpdatingItem.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerScoreQueuedProps }>) => {
        state.amlUpdateStatus = action.payload.data.status;

        if (action.payload.data.status === CustomerScoreUpdateStatus.FAIL) {
          localStorage.removeItem('amlUpdateId');
        }

        if (action.payload.data.status === CustomerScoreUpdateStatus.SUCCESS) {
          localStorage.removeItem('amlUpdateId');
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerLastCalculatingScore
        .matchFulfilled,
      (state, action: PayloadAction<Response<CustomerScore>>) => {
        state.lastCalculatingAml = action.payload.data.edges[0].node;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.createCustomerUserScore.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerScoreQueuedProps }>) => {
        state.amlUpdateStatus = action.payload.data.status;
        localStorage.setItem('amlUpdateId', action.payload.data.id);
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerTransactionsWithWarnings
        .matchPending,
      (state) => {
        state.amlTransactionsErrorsList = undefined;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerTransactionsWithWarnings
        .matchRejected,
      (state, action: any) => {
        state.amlTransactions = undefined;
        state.pagination = undefined;

        if (action?.payload.data) {
          if (Array.isArray(action?.payload.data?.message)) {
            state.amlTransactionsErrorsList = action?.payload.data?.message
              ?.map((item: { value: string }) => item.value)
              .join('; ');
          } else {
            state.amlTransactionsErrorsList = action?.payload.data?.error;
          }
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerTransactionsWithWarnings
        .matchFulfilled,
      (state, action: PayloadAction<Response<BaseTransaction>>) => {
        state.amlTransactions = action.payload.data.edges
          ?.filter((item) => item.node)
          ?.map((item) => item.node);
        state.pagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerOngoingMonitoringItem
        .matchFulfilled,
      (state, action: PayloadAction<{ data: OngoingMonitring }>) => {
        state.ongoingMonitoringItem = action.payload.data;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerOngoingMonitoring
        .matchFulfilled,
      (state, action: PayloadAction<Response<OngoingMonitring>>) => {
        state.ongoingMonitoring = action?.payload?.data?.edges?.map((item) => ({
          ...item.node,
          blocklistNumber: '-',
          matches: '-',
          result: '-',
        }));
        state.ongoingMonitoringPagination = action?.payload?.data?.pageInfo;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.applyCustomerOngoingWarningAction
        .matchFulfilled,
      (state, action: PayloadAction<{ data: TransactionWarning }>) => {
        state.ongoingMonitoring = state.ongoingMonitoring?.map((item) => {
          const result = { ...item };

          const nextWarnings = result.warnings?.edges?.map((warning) => {
            let resultWarning = { ...warning };

            if (resultWarning.node.id === action.payload.data.id) {
              resultWarning = { node: action.payload.data };
            }

            return resultWarning;
          });

          result.warnings = {
            edges: nextWarnings,
            pageInfo: result.warnings?.pageInfo,
          };

          return result;
        });

        if (state.ongoingMonitoringItem) {
          const nextOngoingWarningItem = { ...state.ongoingMonitoringItem };

          const nextOngoingWarningItemWarnings =
            nextOngoingWarningItem.warnings?.edges?.map((item) => {
              let resultWarning = { ...item };

              if (resultWarning.node.id === action.payload.data.id) {
                resultWarning = { node: action.payload.data };
              }

              return resultWarning;
            });

          nextOngoingWarningItem.warnings = {
            edges: nextOngoingWarningItemWarnings,
            pageInfo: nextOngoingWarningItem.warnings?.pageInfo,
          };

          state.ongoingMonitoringItem = nextOngoingWarningItem;
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersAMLScoresHistory
        .matchFulfilled,
      (
        state,
        action: PayloadAction<Response<CustomerScore>> & {
          meta: { arg: { originalArgs: { query?: string } } };
        },
      ) => {
        if (state.aml) {
          let nextAmlChangeLogs: CustomerScore[] = [];

          if (
            action.meta.arg.originalArgs?.query?.includes('cursor') &&
            state.amlChangeLogs
          ) {
            nextAmlChangeLogs = ([] as CustomerScore[]).concat(
              state.amlChangeLogs,
            );
          }

          action.payload.data.edges?.forEach((item) => {
            nextAmlChangeLogs.push(item.node);
          });

          state.amlChangeLogs = nextAmlChangeLogs?.map((item, index) => ({
            ...item,
            order: index + 1,
          }));

          state.amlChangeLogsPagination = action.payload.data.pageInfo;
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.resetCustomerWarningGroupAction
        .matchFulfilled,
      (state, action: PayloadAction<{ data: TransactionWarning }>) => {
        state.ongoingMonitoring = state.ongoingMonitoring?.map((item) => {
          const result = { ...item };

          const nextWarnings = result.warnings?.edges?.map((warning) => {
            let resultWarning = { ...warning };

            if (resultWarning.node.id === action.payload.data.id) {
              resultWarning = {
                node: {
                  ...action.payload.data,
                  action: undefined,
                  comment: undefined,
                  matchStatus: undefined,
                },
              };
            }

            return resultWarning;
          });

          result.warnings = {
            edges: nextWarnings,
            pageInfo: result.warnings?.pageInfo,
          };

          return result;
        });

        if (state.ongoingMonitoringItem) {
          const nextOngoingWarningItem = { ...state.ongoingMonitoringItem };

          const nextOngoingWarningItemWarnings =
            nextOngoingWarningItem.warnings?.edges?.map((item) => {
              let resultWarning = { ...item };

              if (resultWarning.node.id === action.payload.data.id) {
                resultWarning = { node: action.payload.data };
              }

              return resultWarning;
            });

          nextOngoingWarningItem.warnings = {
            edges: nextOngoingWarningItemWarnings,
            pageInfo: nextOngoingWarningItem.warnings?.pageInfo,
          };

          state.ongoingMonitoringItem = nextOngoingWarningItem;
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerWarningGroupWithPagination
        .matchFulfilled,
      (
        state,
        action: PayloadAction<Response<TransactionWarning>> & {
          meta: {
            arg: {
              originalArgs: {
                warningGroupId: string;
              };
            };
          };
        },
      ) => {
        if (state.ongoingMonitoringItem) {
          const result = { ...state.ongoingMonitoringItem };

          if (result.id === action.meta.arg.originalArgs.warningGroupId) {
            const warningResult = { ...result.warnings };

            warningResult.pageInfo = action.payload.data.pageInfo;

            const currentWarnings = result.warnings.edges?.map(
              (warning) => warning.node,
            );

            const nextWarnings = ([] as TransactionWarning[]).concat(
              currentWarnings,
            );

            action?.payload?.data?.edges?.forEach((warning) =>
              nextWarnings.push(warning.node),
            );

            warningResult.edges = nextWarnings?.map((warning) => ({
              node: warning,
            }));

            result.warnings = warningResult;
          }

          state.ongoingMonitoringItem = result;
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.batchApplyCustomerOngoingWarningAction
        .matchFulfilled,
      (
        state,
        action: {
          meta: {
            arg: {
              originalArgs: {
                body: {
                  action: string;
                  comment: string;
                  matchStatus?: string;
                  warningGroupId: string;
                };
              };
            };
          };
        },
      ) => {
        state.ongoingMonitoring = state.ongoingMonitoring?.map(
          (item: OngoingMonitring) => {
            const result = { ...item };

            if (
              result.id === action.meta.arg.originalArgs.body.warningGroupId
            ) {
              const nextWarnings = result.warnings?.edges?.map(
                (warning: { node: TransactionWarning }) => {
                  let resultWarning = { ...warning };

                  resultWarning = {
                    node: {
                      ...resultWarning.node,
                      action: action.meta.arg.originalArgs.body
                        .action as TransactionWarningActionEnum,
                      comment: action.meta.arg.originalArgs.body.comment,
                      matchStatus:
                        action?.meta?.arg?.originalArgs?.body?.matchStatus,
                    },
                  };

                  return resultWarning;
                },
              );

              result.warnings = {
                edges: nextWarnings,
                pageInfo: result.warnings?.pageInfo,
              };
            }

            return result;
          },
        );

        if (state.ongoingMonitoringItem) {
          const nextOngoingWarningItem = { ...state.ongoingMonitoringItem };

          const nextOngoingWarningItemWarnings =
            nextOngoingWarningItem.warnings?.edges?.map((item) => {
              let resultWarning = { ...item };

              resultWarning = {
                node: {
                  ...resultWarning.node,
                  action: action.meta.arg.originalArgs.body
                    .action as TransactionWarningActionEnum,
                  comment: action.meta.arg.originalArgs.body.comment,
                  matchStatus:
                    action?.meta?.arg?.originalArgs?.body?.matchStatus,
                },
              };

              return resultWarning;
            });

          nextOngoingWarningItem.warnings = {
            edges: nextOngoingWarningItemWarnings,
            pageInfo: nextOngoingWarningItem.warnings?.pageInfo,
          };

          state.ongoingMonitoringItem = nextOngoingWarningItem;
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersSourceOfWealth.matchFulfilled,
      (state, action: PayloadAction<Response<DictionaryItem>>) => {
        state.sourceOfWealth = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersSourceOfIncomeDictionary
        .matchFulfilled,
      (state, action: PayloadAction<Response<DictionaryItem>>) => {
        state.sourceOfIncome = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints
        .getCustomersSourceOfWealthForIndividualDictionary.matchFulfilled,
      (state, action: PayloadAction<Response<DictionaryItem>>) => {
        state.sourceOfWealthForIndividual = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersSourceOfFunds.matchFulfilled,
      (state, action: PayloadAction<Response<DictionaryItem>>) => {
        state.customerSourceOfFunds = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getIndividualCustomersAccountsCurrencies
        .matchFulfilled,
      (state, action: PayloadAction<Response<AccountsCurrencies>>) => {
        state.accountsCurrencies = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.createIndividualCustomersAccountsCurrency
        .matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsCurrencies }>) => {
        let nextAccountsCurrencies: AccountsCurrencies[] = [];

        if (state.accountsCurrencies) {
          nextAccountsCurrencies = ([] as AccountsCurrencies[]).concat(
            state.accountsCurrencies,
          );
        }

        nextAccountsCurrencies.push(action.payload.data);

        state.accountsCurrencies = nextAccountsCurrencies;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.updateIndividualCustomersAccountsCurrency
        .matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsCurrencies }>) => {
        if (state.accountsCurrencies) {
          state.accountsCurrencies = state.accountsCurrencies?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = action.payload.data;
            }

            return result;
          });
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.deleteIndividualCustomersAccountsCurrency
        .matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsCurrencies }>) => {
        if (state.accountsCurrencies) {
          state.accountsCurrencies = state.accountsCurrencies?.filter(
            (item) => item.id !== action.payload.data.id,
          );
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersAccountsCurrencies
        .matchFulfilled,
      (state, action: PayloadAction<Response<AccountsCurrencies>>) => {
        state.accountsCurrencies = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.createCustomersAccountsCurrency
        .matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsCurrencies }>) => {
        let nextAccountsCurrencies: AccountsCurrencies[] = [];

        if (state.accountsCurrencies) {
          nextAccountsCurrencies = ([] as AccountsCurrencies[]).concat(
            state.accountsCurrencies,
          );
        }

        nextAccountsCurrencies.push(action.payload.data);

        state.accountsCurrencies = nextAccountsCurrencies;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.updateCustomersAccountsCurrency
        .matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsCurrencies }>) => {
        if (state.accountsCurrencies) {
          state.accountsCurrencies = state.accountsCurrencies?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = action.payload.data;
            }

            return result;
          });
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.deleteCustomersAccountsCurrency
        .matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsCurrencies }>) => {
        if (state.accountsCurrencies) {
          state.accountsCurrencies = state.accountsCurrencies?.filter(
            (item) => item.id !== action.payload.data.id,
          );
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getIndividualCustomersAccountsFlows
        .matchFulfilled,
      (state, action: PayloadAction<Response<AccountsFlows>>) => {
        state.accountsFlows = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.createIndividualCustomersAccountsFlow
        .matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsFlows }>) => {
        let nextAccountsFlows: AccountsFlows[] = [];

        if (state.accountsFlows) {
          nextAccountsFlows = ([] as AccountsFlows[]).concat(
            state.accountsFlows,
          );
        }

        nextAccountsFlows.push(action.payload.data);

        state.accountsFlows = nextAccountsFlows;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.updateIndividualCustomersAccountsFlow
        .matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsFlows }>) => {
        if (state.accountsFlows) {
          state.accountsFlows = state.accountsFlows?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = action.payload.data;
            }

            return result;
          });
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.deleteIndividualCustomersAccountsFlow
        .matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsFlows }>) => {
        if (state.accountsFlows) {
          state.accountsFlows = state.accountsFlows?.filter(
            (item) => item.id !== action.payload.data.id,
          );
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersAccountsFlows.matchFulfilled,
      (state, action: PayloadAction<Response<AccountsFlows>>) => {
        state.accountsFlows = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.createCustomersAccountsFlow
        .matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsFlows }>) => {
        let nextAccountsFlows: AccountsFlows[] = [];

        if (state.accountsFlows) {
          nextAccountsFlows = ([] as AccountsFlows[]).concat(
            state.accountsFlows,
          );
        }

        nextAccountsFlows.push(action.payload.data);

        state.accountsFlows = nextAccountsFlows;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.updateCustomersAccountsFlow
        .matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsFlows }>) => {
        if (state.accountsFlows) {
          state.accountsFlows = state.accountsFlows?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = action.payload.data;
            }

            return result;
          });
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.deleteCustomersAccountsFlow
        .matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsFlows }>) => {
        if (state.accountsFlows) {
          state.accountsFlows = state.accountsFlows?.filter(
            (item) => item.id !== action.payload.data.id,
          );
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getIndividualCustomersExpectedFundsOrigins
        .matchFulfilled,
      (state, action: PayloadAction<Response<ExpectedFundsOrigins>>) => {
        state.expectedFundsOrigins = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints
        .createIndividualCustomersExpectedFundsOrigins.matchFulfilled,
      (state, action: PayloadAction<{ data: ExpectedFundsOrigins }>) => {
        let nextExpectedFundsOrigins: ExpectedFundsOrigins[] = [];

        if (state.expectedFundsOrigins) {
          nextExpectedFundsOrigins = ([] as ExpectedFundsOrigins[]).concat(
            state.expectedFundsOrigins,
          );
        }

        nextExpectedFundsOrigins.push(action.payload.data);

        state.expectedFundsOrigins = nextExpectedFundsOrigins;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints
        .updateIndividualCustomersExpectedFundsOrigins.matchFulfilled,
      (state, action: PayloadAction<{ data: ExpectedFundsOrigins }>) => {
        if (state.expectedFundsOrigins) {
          state.expectedFundsOrigins = state.expectedFundsOrigins?.map(
            (item) => {
              let result = { ...item };

              if (result.id === action.payload.data.id) {
                result = action.payload.data;
              }

              return result;
            },
          );
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints
        .deleteIndividualCustomersExptectedFundsOrigins.matchFulfilled,
      (state, action: PayloadAction<{ data: ExpectedFundsOrigins }>) => {
        if (state.expectedFundsOrigins) {
          state.expectedFundsOrigins = state.expectedFundsOrigins?.filter(
            (item) => item.id !== action.payload.data.id,
          );
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersExpectedFundsOrigins
        .matchFulfilled,
      (state, action: PayloadAction<Response<ExpectedFundsOrigins>>) => {
        state.expectedFundsOrigins = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.createCustomersExpectedFundsOrigins
        .matchFulfilled,
      (state, action: PayloadAction<{ data: ExpectedFundsOrigins }>) => {
        let nextExpectedFundsOrigins: ExpectedFundsOrigins[] = [];

        if (state.expectedFundsOrigins) {
          nextExpectedFundsOrigins = ([] as ExpectedFundsOrigins[]).concat(
            state.expectedFundsOrigins,
          );
        }

        nextExpectedFundsOrigins.push(action.payload.data);

        state.expectedFundsOrigins = nextExpectedFundsOrigins;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.updateCustomersExpectedFundsOrigins
        .matchFulfilled,
      (state, action: PayloadAction<{ data: ExpectedFundsOrigins }>) => {
        if (state.expectedFundsOrigins) {
          state.expectedFundsOrigins = state.expectedFundsOrigins?.map(
            (item) => {
              let result = { ...item };

              if (result.id === action.payload.data.id) {
                result = action.payload.data;
              }

              return result;
            },
          );
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.deleteCustomersExptectedFundsOrigins
        .matchFulfilled,
      (state, action: PayloadAction<{ data: ExpectedFundsOrigins }>) => {
        if (state.expectedFundsOrigins) {
          state.expectedFundsOrigins = state.expectedFundsOrigins?.filter(
            (item) => item.id !== action.payload.data.id,
          );
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersCRSAndFacta.matchFulfilled,
      (state, action: PayloadAction<Response<CRSAndFatca>>) => {
        state.crsAndFatca = action.payload.data.edges?.map((item) => item.node);
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.createCustomersCRSAndFacta.matchFulfilled,
      (state, action: PayloadAction<{ data: CRSAndFatca }>) => {
        let nextCRSAndFatca: CRSAndFatca[] = [];

        if (state.crsAndFatca) {
          nextCRSAndFatca = ([] as CRSAndFatca[]).concat(state.crsAndFatca);
        }

        nextCRSAndFatca.push(action.payload.data);

        state.crsAndFatca = nextCRSAndFatca;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.updateCustomersCRSAndFacta.matchFulfilled,
      (state, action: PayloadAction<{ data: CRSAndFatca }>) => {
        if (state.crsAndFatca) {
          state.crsAndFatca = state.crsAndFatca?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = action.payload.data;
            }

            return result;
          });
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.deleteCustomersCRSAndFacta.matchFulfilled,
      (state, action: PayloadAction<{ data: CRSAndFatca }>) => {
        if (state.crsAndFatca) {
          state.crsAndFatca = state.crsAndFatca?.filter(
            (item) => item.id !== action.payload.data.id,
          );
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCOFCustomersCRSAndFacta.matchFulfilled,
      (state, action: PayloadAction<Response<CRSAndFatca>>) => {
        state.crsAndFatca = action.payload.data.edges?.map((item) => item.node);
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.createCOFCustomersCRSAndFacta
        .matchFulfilled,
      (state, action: PayloadAction<{ data: CRSAndFatca }>) => {
        let nextCRSAndFatca: CRSAndFatca[] = [];

        if (state.crsAndFatca) {
          nextCRSAndFatca = ([] as CRSAndFatca[]).concat(state.crsAndFatca);
        }

        nextCRSAndFatca.push(action.payload.data);

        state.crsAndFatca = nextCRSAndFatca;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.updateCOFCustomersCRSAndFacta
        .matchFulfilled,
      (state, action: PayloadAction<{ data: CRSAndFatca }>) => {
        if (state.crsAndFatca) {
          state.crsAndFatca = state.crsAndFatca?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = action.payload.data;
            }

            return result;
          });
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.deleteCOFCustomersCRSAndFacta
        .matchFulfilled,
      (state, action: PayloadAction<{ data: CRSAndFatca }>) => {
        if (state.crsAndFatca) {
          state.crsAndFatca = state.crsAndFatca?.filter(
            (item) => item.id !== action.payload.data.id,
          );
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersAffilatedCompanies
        .matchFulfilled,
      (state, action: PayloadAction<Response<AffilatedCompany>>) => {
        state.affiliatedCompanies = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.createCustomersAffilatedCompanies
        .matchFulfilled,
      (state, action: PayloadAction<{ data: AffilatedCompany }>) => {
        let nextAffiliatedCompanies: AffilatedCompany[] = [];

        if (state.affiliatedCompanies) {
          nextAffiliatedCompanies = ([] as AffilatedCompany[]).concat(
            state.affiliatedCompanies,
          );
        }

        nextAffiliatedCompanies.push(action.payload.data);

        state.affiliatedCompanies = nextAffiliatedCompanies;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.updateCustomersAffilatedCompanies
        .matchFulfilled,
      (state, action: PayloadAction<{ data: AffilatedCompany }>) => {
        if (state.affiliatedCompanies) {
          state.affiliatedCompanies = state.affiliatedCompanies?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = action.payload.data;
            }

            return result;
          });
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.deleteCustomersAffilatedCompanies
        .matchFulfilled,
      (state, action: PayloadAction<{ data: AffilatedCompany }>) => {
        if (state.affiliatedCompanies) {
          state.affiliatedCompanies = state.affiliatedCompanies?.filter(
            (item) => item.id !== action.payload.data.id,
          );
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersMonitoringRules.matchPending,
      (state) => {
        state.monitoringRulesErrorsList = undefined;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersMonitoringRules.matchRejected,
      (state, action: any) => {
        state.monitoringRules = undefined;

        if (action?.payload.data) {
          if (Array.isArray(action?.payload.data?.message)) {
            state.monitoringRulesErrorsList = action?.payload.data?.message
              ?.map((item: { value: string }) => item.value)
              .join('; ');
          } else {
            state.monitoringRulesErrorsList = action?.payload.data?.error;
          }
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersMonitoringRules
        .matchFulfilled,
      (state, action: PayloadAction<Response<TransactionMonitoringRule>>) => {
        state.monitoringRules = action?.payload.data?.edges?.map(
          (item) => item.node,
        );
      },
    );
  },
});

export const { clearAmlState, clearAmlChangeLogs } = customerAmlSlice.actions;

export default customerAmlSlice.reducer;
